import { EthAddress } from '@xylabs/sdk-js'
import { TypedEvent, XyoWorldMinterEvents } from '@xyo-network/world-typechain'

import { XyoWorldBase } from '../../../Base'
import { BaseJsonObject } from '../../../models'
import { Quadkey } from '../../../Quadkey'
import { MinterDeployBlocks } from '../DeployBlocks'
import { MinterContracts } from '../MinterContracts'
import { EventsV1 } from './v1'
import { EventsV2 } from './v2'
import { EventsV3 } from './v3'

export * from './helpers'

export interface MinterEventsJsonObject extends BaseJsonObject {
  blockNumber?: number
  events?: TypedEvent[]
}

class MinterEvents extends XyoWorldBase<MinterEventsJsonObject> {
  public bidder?: EthAddress

  public minters: MinterContracts

  public quadkey?: Quadkey

  public v1?: EventsV1

  public v2?: EventsV2

  public v3: EventsV3

  private _blockNumber?: number

  private _events?: TypedEvent[]

  private onNewEvent?: (event: TypedEvent) => void

  constructor(minters: MinterContracts, filter?: { bidder?: EthAddress; quadkey?: Quadkey }, jsonObject?: MinterEventsJsonObject) {
    super()
    this.quadkey = filter?.quadkey
    this.bidder = filter?.bidder
    this.minters = minters
    this.v1 = minters.v1 ? new EventsV1(minters.v1?.contract, filter) : undefined
    this.v2 = minters.v2 ? new EventsV2(minters.v2?.contract, filter) : undefined
    this.v3 = new EventsV3(minters.current.contract, filter)
    this.loadJsonObject(jsonObject)
  }

  public async all(fromBlockOrBlockHash = MinterDeployBlocks.v1, toBlock?: number) {
    const promises: Promise<TypedEvent[]>[] = []
    if (fromBlockOrBlockHash ?? 0 < MinterDeployBlocks.v2) {
      promises.push(this.v1?.all(fromBlockOrBlockHash, toBlock) ?? new Promise<TypedEvent[]>(() => []))
    }
    if (fromBlockOrBlockHash ?? 0 < MinterDeployBlocks.v3) {
      promises.push(this.v2?.all(fromBlockOrBlockHash, toBlock) ?? new Promise<TypedEvent[]>(() => []))
    }
    promises.push(this.v3.all(fromBlockOrBlockHash, toBlock))
    this._events = (await Promise.all(promises)).flat()
    return this._events
  }

  public async auctionStarted(fromBlockOrBlockHash?: number | string, toBlock?: number) {
    const promises: Promise<XyoWorldMinterEvents.AuctionStarted[]>[] = []
    promises.push(this.v1?.auctionStarted(fromBlockOrBlockHash, toBlock) ?? new Promise<TypedEvent[]>(() => []))
    promises.push(this.v2?.auctionStarted(fromBlockOrBlockHash, toBlock) ?? new Promise<TypedEvent[]>(() => []))
    promises.push(this.v3.auctionStarted(fromBlockOrBlockHash, toBlock))
    return (await Promise.all(promises)).flat()
  }

  public async bidPlaced(fromBlockOrBlockHash?: number | string, toBlock?: number): Promise<XyoWorldMinterEvents.BidPlaced[]> {
    const promises: Promise<XyoWorldMinterEvents.BidPlaced[]>[] = []
    promises.push(this.v1?.bidPlaced(fromBlockOrBlockHash, toBlock) ?? new Promise<TypedEvent[]>(() => []))
    promises.push(this.v2?.bidPlaced(fromBlockOrBlockHash, toBlock) ?? new Promise<TypedEvent[]>(() => []))
    promises.push(this.v3.bidPlaced(fromBlockOrBlockHash, toBlock))
    return (await Promise.all(promises)).flat()
  }

  public async geoTokenMinted(fromBlockOrBlockHash?: number | string, toBlock?: number) {
    const promises: Promise<XyoWorldMinterEvents.GeotokenMinted[]>[] = []
    promises.push(this.v1?.geotokenMinted(fromBlockOrBlockHash, toBlock) ?? new Promise<TypedEvent[]>(() => []))
    promises.push(this.v2?.geotokenMinted(fromBlockOrBlockHash, toBlock) ?? new Promise<TypedEvent[]>(() => []))
    promises.push(this.v3.geotokenMinted(fromBlockOrBlockHash, toBlock))
    return (await Promise.all(promises)).flat()
  }

  public load(events: TypedEvent[], blockNumber?: number) {
    this._events = events
    this._blockNumber = blockNumber
  }

  public loadJsonObject(jsonObject?: MinterEventsJsonObject) {
    if (jsonObject) {
      this._events = jsonObject.events
      this._blockNumber = jsonObject.blockNumber
    }
    return this
  }

  public async mintingFeeSent(fromBlockOrBlockHash?: number | string, toBlock?: number) {
    const promises: Promise<XyoWorldMinterEvents.MintingFeeSent[]>[] = []
    promises.push(this.v2?.mintingFeeSent(fromBlockOrBlockHash, toBlock) ?? new Promise<TypedEvent[]>(() => []))
    promises.push(this.v3.mintingFeeSent(fromBlockOrBlockHash, toBlock) ?? new Promise<TypedEvent[]>(() => []))
    return (await Promise.all(promises)).flat()
  }

  public monitorEvents(onNewEvent?: (event: TypedEvent) => void) {
    this.onNewEvent = onNewEvent
    /* this.minters.current.contract.addListener('*', (event: Event) => {
      const minterEvent = parseEvent(event, 3)
      this.onNewEvent?.(minterEvent)
    }) */
  }

  public async refundedCurrentBid(fromBlockOrBlockHash?: number | string, toBlock?: number) {
    const promises: Promise<XyoWorldMinterEvents.RefundedCurrentBid[]>[] = []
    promises.push(this.v1?.refundedCurrentBid(fromBlockOrBlockHash, toBlock) ?? new Promise<TypedEvent[]>(() => []))
    promises.push(this.v2?.refundedCurrentBid(fromBlockOrBlockHash, toBlock) ?? new Promise<TypedEvent[]>(() => []))
    promises.push(this.v3.refundedCurrentBid(fromBlockOrBlockHash, toBlock))
    return (await Promise.all(promises)).flat()
  }

  public toJsonObject(): MinterEventsJsonObject {
    return {
      blockNumber: this._blockNumber,
      events: this._events,
    }
  }

  public async tokensBurned(fromBlockOrBlockHash?: number | string, toBlock?: number) {
    const promises: Promise<XyoWorldMinterEvents.TokensBurned[]>[] = []
    promises.push(this.v1?.tokensBurned(fromBlockOrBlockHash, toBlock) ?? new Promise<TypedEvent[]>(() => []))
    promises.push(this.v2?.tokensBurned(fromBlockOrBlockHash, toBlock) ?? new Promise<TypedEvent[]>(() => []))
    promises.push(this.v3.tokensBurned(fromBlockOrBlockHash, toBlock))
    return (await Promise.all(promises)).flat()
  }

  public async tokensFlowed(fromBlockOrBlockHash?: number | string, toBlock?: number) {
    const promises: Promise<XyoWorldMinterEvents.TokensFlowed[]>[] = []
    promises.push(this.v1?.tokensFlowed(fromBlockOrBlockHash, toBlock) ?? new Promise<TypedEvent[]>(() => []))
    promises.push(this.v2?.tokensFlowed(fromBlockOrBlockHash, toBlock) ?? new Promise<TypedEvent[]>(() => []))
    promises.push(this.v3.tokensFlowed(fromBlockOrBlockHash, toBlock))
    return (await Promise.all(promises)).flat()
  }
}

export { MinterEvents }
